import { useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useAppForm } from "~/components/ui/form";
import { parseValidationErrors } from "~/lib/errors";
import { sessionQueryOptions } from "~/modules/auth/functions/session";
import { useTRPC } from "~/trpc/client";
import { type AuthLoginInput, authLoginSchema } from "./login.schema";

export function useAuthLoginForm({
	defaultValues,
}: {
	defaultValues?: Partial<AuthLoginInput>;
} = {}) {
	const { t } = useLingui();
	const queryClient = useQueryClient();
	const trpc = useTRPC();
	const router = useRouter();

	const authLoginMutation = useMutation(
		trpc.auth.login.mutationOptions({
			async onSuccess() {
				// Invalidate queries to ensure fresh data
				await queryClient.invalidateQueries();

				// Fetch the updated session to confirm login worked
				await queryClient.fetchQuery(sessionQueryOptions());

				// Invalidate router loaders which will trigger root's beforeLoad
				// with updated session, and root can check the search param to redirect
				await router.invalidate();
			},
		}),
	);

	return useAppForm({
		defaultValues: {
			email: "",
			password: "",
			...defaultValues,
		},
		canSubmitWhenInvalid: true,
		validators: {
			onSubmit: authLoginSchema,
			onSubmitAsync: async ({ value }) => {
				try {
					await authLoginMutation.mutateAsync(value);
				} catch (e) {
					return parseValidationErrors(e, t`Something went wrong.`);
				}
			},
		},
	});
}
